import { useState } from 'react';
import { Link } from 'react-router-dom';
import JaLayout from '@/components/feature/JaLayout';
import useSEO from '@/hooks/useSEO';

const partnerTypes = [
  {
    icon: 'ri-store-2-line',
    title: '新規ブランド立上げ',
    desc: 'ゼロからスタートする新規チームを支援。処方開発・パッケージデザイン・少量試験生産まで、自社ブランドの立ち上げをスピーディーにサポートします。',
    tags: ['少量対応', '処方開発', 'ブランド構築'],
    color: '#4CAF50',
  },
  {
    icon: 'ri-shopping-bag-line',
    title: 'EC事業者',
    desc: '短納期・柔軟なロット数でEC事業の機動的な新商品投入ニーズに対応。市場で売れるヒット商品づくりをサポートします。',
    tags: ['短納期', '柔軟なロット', 'EC向け'],
    color: '#66BB6A',
  },
  {
    icon: 'ri-hospital-line',
    title: '薬局・薬系チャネル',
    desc: '薬局チャネルの法規要件に精通し、健康食品規制に適合した製品を提供。チャネル競争力を強化します。',
    tags: ['法規適合', 'チャネル対応', '品質保証'],
    color: '#81C784',
  },
  {
    icon: 'ri-global-line',
    title: '海外・国際ブランド',
    desc: '多国輸出の豊富な実績を活かし、国際ブランドの台湾進出や海外展開を支援。輸出規制に適合した製品をご提供します。',
    tags: ['多国輸出', '法規サポート', 'グローバル市場'],
    color: '#A5D6A7',
  },
];

const suitableList = [
  '自社健康食品ブランドを構築したいが、生産設備を持っていない',
  'すでにブランドがあり、安定した高品質の製造パートナーを探している',
  '新商品を素早く市場投入し、開発期間を短縮したい',
  '異なる製品ラインに対応できる多種類の剤型が必要',
  '法規申請・表示設計のワンストップサービスを希望している',
  '海外市場への展開を計画し、輸出規制に適合した製品が必要',
  '独自の処方コンセプトがあり、専門チームによる実現サポートを求めている',
  '少量試験生産からスタートし、段階的に市場拡大していきたい',
];

const notSuitable = [
  '医療機器・医薬品の製造（当社サービス対象外）',
];

const steps = [
  {
    num: '01', icon: 'ri-discuss-line', title: '初回相談',
    desc: 'オンラインフォームまたは営業担当へご連絡ください。製品ニーズ・ターゲット市場・予算をお知らせいただければ、1営業日以内にご返答します。',
    duration: '1営業日',
  },
  {
    num: '02', icon: 'ri-file-list-3-line', title: '要件確認',
    desc: '担当者がお客様と詳細なヒアリングを実施。製品タイプ・剤型・処方方向・包装仕様・法規要件などを確認します。',
    duration: '3〜5営業日',
  },
  {
    num: '03', icon: 'ri-draft-line', title: '処方提案',
    desc: 'R&Dチームがご要件に基づき処方提案書を作成。原料説明・機能性訴求・参考仕様・初期見積もりを含みます。',
    duration: '5〜10営業日',
  },
  {
    num: '04', icon: 'ri-test-tube-line', title: 'サンプル製作',
    desc: '処方確認後、少量試作サンプルを製作。品質・外観・風味をご確認いただき、調整も可能です。',
    duration: '2〜4週間',
  },
  {
    num: '05', icon: 'ri-file-check-line', title: '契約締結',
    desc: 'サンプル承認後、正式に提携契約を締結。発注数量・パッケージデザイン・納期・支払条件などを確定します。',
    duration: '1〜2週間',
  },
  {
    num: '06', icon: 'ri-settings-3-line', title: '量産製造',
    desc: '契約に従い本格量産を開始。全工程で品質管理を監視し、完成品がサンプル仕様と品質基準に適合することを確認します。',
    duration: '4〜8週間',
  },
  {
    num: '07', icon: 'ri-checkbox-circle-line', title: 'QC検査・出荷前確認',
    desc: '生産完了後に全面的な品質検査を実施し、完全なバッチ検査報告書を発行。安全基準への適合を確認後、出荷準備を行います。',
    duration: '3〜5営業日',
  },
  {
    num: '08', icon: 'ri-truck-line', title: '出荷・納品',
    desc: '合意したスケジュールで出荷し、完全な出荷書類をご提供。その後も市場フィードバックの追跡やテクニカルサポートを継続して提供します。',
    duration: '合意による',
  },
];

const modes = [
  {
    mode: 'ODM',
    fullName: 'Original Design Manufacturing',
    title: 'ODM 委託設計製造',
    color: '#4CAF50',
    bgColor: '#f0faf0',
    borderColor: '#c8e6c9',
    desc: '康永生技が処方設計・原料選定・製品仕様を一括提供。お客様はブランドポジショニングと訴求ポイントをお知らせいただくだけで、完成品を受け取れます。',
    suitable: '新規ブランドや素早く新商品を投入したい販売チャネルに最適',
    points: [
      '既存処方またはカスタム処方からお選びいただけます',
      'R&Dチームがご要件に応じて処方を調整',
      '原料調達・生産・品質管理のフルプロセスを含む',
      'お客様のプライベートブランドラベルを貼付可能',
      '製品開発リードタイムを短縮',
    ],
  },
  {
    mode: 'OEM',
    fullName: 'Original Equipment Manufacturing',
    title: 'OEM 委託製造',
    color: '#2e7d32',
    bgColor: '#e8f5e9',
    borderColor: '#a5d6a7',
    desc: 'お客様が完全な処方仕様と技術文書をご提供いただき、康永生技が製造を担当。お客様の要件どおりに精密に製造し、安定した品質を保証します。',
    suitable: '既存処方を持ち、安定した製造パートナーを求める既存ブランドに最適',
    points: [
      'お客様が完全な処方と仕様を提供',
      '康永が仕様に従って製造',
      '厳格な品質管理でバッチ間の一貫性を確保',
      'NDA契約でお客様の処方の秘密を保護',
      '多種類の剤型・包材仕様に対応',
    ],
  },
];

const advantages = [
  { icon: 'ri-flask-line', title: '処方開発サポート', desc: '機能性訴求から原料選定まで、専門R&Dチームによる充実した技術サポートを提供します。' },
  { icon: 'ri-price-tag-3-line', title: '競争力のある価格', desc: '自社工場と原料調達の優位性を活かし、業界で競争力のある製造価格でブランドコストを削減します。' },
  { icon: 'ri-time-line', title: '短納期対応', desc: '綿密な生産スケジュール管理により、サンプルから量産まで市場投入までの時間を効果的に短縮します。' },
  { icon: 'ri-shield-check-line', title: '品質保証', desc: '全ロットに完全な品質管理報告書を発行。GMP工場生産により、安定した品質を保証します。' },
  { icon: 'ri-file-text-line', title: '法規サポート', desc: '健康食品法規に精通し、製品表示審査・法規申請をサポート。適法な市場投入を確保します。' },
  { icon: 'ri-lock-line', title: '機密保護', desc: '厳格なNDA秘密保持契約を事前に締結。お客様の処方とビジネス情報を万全に保護します。' },
];

const faqs = [
  {
    q: '最低発注数量（MOQ）はどのくらいですか？',
    a: '剤型・処方によって異なります。一般的にカプセル・錠剤製品のMOQは約3,000〜5,000本、粉末パック・飲料は約5,000個からとなります。具体的なご要件は営業担当までお問い合わせください。',
  },
  {
    q: '製品のコンセプトはあるが処方がない場合でも依頼できますか？',
    a: 'もちろんです！康永生技は完全なODMサービスを提供しています。ブランドポジショニング・ターゲット層・機能性訴求をお知らせいただければ、R&Dチームがカスタム処方を開発します。技術的な背景は不要です。',
  },
  {
    q: '提携中、私の処方は保護されますか？',
    a: '提携開始前に厳格なNDA（秘密保持契約）を締結します。お客様の処方・事業計画・すべての技術情報は完全に保護され、外部に共有されることはありません。',
  },
  {
    q: 'サンプル確認から量産まどのくらいの時間がかかりますか？',
    a: '初回相談からサンプル完成まで通常3〜6週間。サンプル承認後、受注量と製品の複雑さに応じて量産には約4〜8週間かかります。',
  },
  {
    q: 'パッケージデザインと表示審査のサポートは受けられますか？',
    a: 'はい。康永はパッケージデザインの提案と表示内容の審査サービスを提供しています。台湾の健康食品関連法規への適合を確認し、適法な販売が可能な製品づくりをサポートします。',
  },
  {
    q: '提携後の製品品質はどのように保証されますか？',
    a: '全生産ロットで微生物・重金属・崩壊度などの安全指標を含む包括的な品質検査を実施し、完全なバッチ検査報告書を発行。すべての製品が仕様基準に適合していることを確認します。',
  },
  {
    q: '製品の輸出に対応できますか？',
    a: 'はい。康永生技は多国輸出の豊富な実績を持ち、各国の法規の違いに精通しています。輸出に必要な書類の準備をサポートし、ターゲット市場の要件への適合を確認します。',
  },
  {
    q: '初めての依頼はどうすればよいですか？',
    a: '最も簡単な方法は、ウェブサイトのお問い合わせフォーム、LINE、またはお電話で営業チームに初期要件をお知らせいただくことです。担当者が1営業日以内に返答し、詳細な打ち合わせを設定します。',
  },
];

const contactOptions = [
  {
    icon: 'ri-chat-1-line', label: 'LINEでお問い合わせ',
    desc: 'QRコードをスキャンまたはリンクをクリックしてLINEに追加し、担当者と直接チャットできます。',
    action: 'LINEに追加する', href: 'https://lin.ee/X7Hcpf4', color: '#4CAF50', bg: '#f0faf0',
  },
  {
    icon: 'ri-phone-line', label: 'お電話でのご相談',
    desc: '月〜金 09:00〜18:00、担当者がお電話でご相談を承ります。',
    action: 'お電話する', href: 'tel:04-22029858', color: '#2e7d32', bg: '#e8f5e9',
  },
  {
    icon: 'ri-mail-line', label: 'メールでお見積り',
    desc: 'ご要件をメールでお送りください。1営業日以内にご返答・お見積りを差し上げます。',
    action: 'メールを送る', href: 'mailto:natural1070606@gmail.com', color: '#388e3c', bg: '#f1f8e9',
  },
];

export default function JaPartnershipPage() {
  const [openIdx, setOpenIdx] = useState<number | null>(null);

  useSEO({
    title: 'OEM/ODM提携について｜康永生技｜台湾健康食品製造',
    description: '康永生技とOEM/ODM提携を始めましょう。少ロット対応、短納期、GMP認定工場、全面的な法規サポートで世界中のブランドを支援します。',
    keywords: 'OEM ODM提携 台湾, 健康食品製造パートナー, 少ロットサプリメント, GMP受託製造',
    lang: 'ja',
    canonical: 'https://kangyong.com/ja/partnership',
  });

  return (
    <JaLayout>
      {/* Hero */}
      <section className="relative pt-20 min-h-[440px] md:min-h-[500px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=dark%20aerial%20view%20of%20lush%20dense%20deep%20green%20tropical%20forest%20canopy%20with%20dramatic%20mist%20and%20moody%20low%20light%2C%20rich%20dark%20emerald%20tones%20with%20deep%20shadows%2C%20cinematic%20nature%20landscape%20perfect%20for%20text%20overlay%20contrast&width=1920&height=500&seq=partner-hero-bg-03&orientation=landscape"
            alt="康永生技パートナーシップ"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0d2210]/92 via-[#0d2210]/72 to-[#0d2210]/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0d2210]/75 via-transparent to-[#0d2210]/30" />
        </div>
        <div className="relative z-10 w-full max-w-screen-xl mx-auto px-6 lg:px-10 pb-16 pt-28">
          <div className="max-w-2xl">
            <span className="inline-block bg-[#4CAF50]/25 border border-[#4CAF50]/50 text-green-300 text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5 tracking-wide">
              提携について
            </span>
            <h1 className="text-white font-bold text-[2.25rem] md:text-[3rem] leading-tight mb-5">
              <span className="block">提携を始め、</span>
              <span className="block text-green-300">ブランド成長の新たな可能性を</span>
            </h1>
            <p className="text-gray-200 text-[1.0625rem] leading-[1.75] max-w-xl mb-8">
              新規ブランドでも、EC事業者でも、従来型小売業者でも、康永生技は最適なODM/OEMパートナーシップを提供し、製品の早期市場投入を実現します。
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/ja/contact/form" className="whitespace-nowrap bg-[#4CAF50] hover:bg-[#43a047] text-white font-semibold text-[1rem] px-7 py-3.5 rounded-full transition-all duration-300 cursor-pointer">
                今すぐ相談する
              </Link>
              <Link to="/ja/services" className="whitespace-nowrap bg-white/15 hover:bg-white/25 border border-white/60 text-white font-semibold text-[1rem] px-7 py-3.5 rounded-full transition-all duration-300 cursor-pointer">
                サービスを見る
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Partner Types */}
      <section className="py-20 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">
              提携対象
            </span>
            <h2 className="text-[#1a2e1a] font-bold mb-4">どのようなパートナーに対応していますか？</h2>
            <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">
              康永生技のサービス範囲は幅広く、どのフェーズにあっても最適なOEM/ODMソリューションをご提案できます。
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {partnerTypes.map((p) => (
              <div key={p.title} className="bg-[#f7faf7] rounded-2xl p-7 hover:border-green-200 border border-transparent hover:-translate-y-1 transition-all duration-300">
                <div className="w-14 h-14 flex items-center justify-center rounded-xl mb-5" style={{ backgroundColor: `${p.color}20` }}>
                  <i className={`${p.icon} text-2xl`} style={{ color: p.color }} />
                </div>
                <h3 className="text-[#1a2e1a] font-bold text-[1.0625rem] mb-3">{p.title}</h3>
                <p className="text-gray-500 text-[0.9375rem] leading-[1.75] mb-4">{p.desc}</p>
                <div className="flex flex-wrap gap-2">
                  {p.tags.map((tag) => (
                    <span key={tag} className="text-[0.75rem] px-2.5 py-1 rounded-full font-medium" style={{ backgroundColor: `${p.color}15`, color: p.color }}>{tag}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Suitable For */}
      <section className="py-20 bg-[#f7faf7]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-start">
            <div className="w-full lg:w-1/2">
              <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5 border border-green-200">
                対象となる方
              </span>
              <h2 className="text-[#1a2e1a] font-bold leading-tight mb-6">
                康永との提携に<br />適しているのはどんなブランドですか？
              </h2>
              <p className="text-gray-500 text-[1.0625rem] leading-[1.75] mb-8">
                以下のいずれかに当てはまれば、康永生技が理想的な製造パートナーです：
              </p>
              <div className="space-y-3 mb-8">
                {suitableList.map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-checkbox-circle-fill text-[#4CAF50] text-lg" />
                    </div>
                    <span className="text-[#1a2e1a] text-[0.9375rem] leading-[1.75]">{item}</span>
                  </div>
                ))}
              </div>
              {notSuitable.length > 0 && (
                <div className="bg-orange-50 border border-orange-100 rounded-xl p-5">
                  <p className="text-orange-700 font-semibold text-[0.9375rem] mb-2 flex items-center gap-2">
                    <i className="ri-information-line text-lg" /> サービス範囲についての注意
                  </p>
                  {notSuitable.map((item) => (
                    <p key={item} className="text-orange-600 text-[0.875rem] leading-[1.75]">・{item}</p>
                  ))}
                </div>
              )}
              <div className="mt-8">
                <Link to="/ja/contact/form" className="whitespace-nowrap inline-flex items-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-semibold text-[1rem] px-7 py-4 rounded-full transition-all duration-300 cursor-pointer">
                  提携できるか相談する <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
            <div className="w-full lg:w-1/2 flex-shrink-0">
              <div className="relative rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=modern%20bright%20supplement%20manufacturing%20facility%20interior%20with%20large%20windows%20shelves%20full%20of%20products%2C%20clean%20professional%20health%20food%20production%20environment%2C%20workers%20in%20white%20uniforms%2C%20natural%20daylight&width=720&height=540&seq=suitable-img-01&orientation=landscape"
                  alt="提携対象"
                  className="w-full h-[360px] lg:h-[460px] object-cover object-top rounded-2xl"
                />
                <div className="absolute bottom-5 left-5 bg-white/95 rounded-xl px-5 py-4 flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-green-50 rounded-lg">
                    <i className="ri-user-heart-line text-[#4CAF50] text-xl" />
                  </div>
                  <div>
                    <p className="text-[#1a2e1a] font-bold text-[0.9375rem]">600以上のブランドが信頼</p>
                    <p className="text-gray-400 text-[0.75rem]">あらゆるタイプのパートナーに対応</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cooperation Process — 8 Steps */}
      <section className="py-20 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">
              提携フロー
            </span>
            <h2 className="text-[#1a2e1a] font-bold mb-4">8ステップ、相談から納品まで</h2>
            <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">
              明確で透明なプロセスにより、すべてのフェーズで製品の進捗を安心して把握できます。
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {steps.map((s, idx) => (
              <div key={s.num} className="relative bg-[#f7faf7] rounded-2xl p-6 border border-gray-100 hover:border-green-200 hover:-translate-y-1 transition-all duration-300">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-[#4CAF50] font-bold text-[1.5rem] leading-none opacity-30">{s.num}</span>
                  <div className="w-10 h-10 flex items-center justify-center bg-white rounded-xl border border-green-100">
                    <i className={`${s.icon} text-[#4CAF50] text-lg`} />
                  </div>
                </div>
                <h3 className="text-[#1a2e1a] font-bold text-[1rem] mb-2">{s.title}</h3>
                <p className="text-gray-500 text-[0.875rem] leading-[1.75] mb-4">{s.desc}</p>
                <div className="flex items-center gap-1.5">
                  <i className="ri-time-line text-[#4CAF50] text-sm" />
                  <span className="text-[#4CAF50] text-[0.8125rem] font-medium">{s.duration}</span>
                </div>
                {idx < steps.length - 1 && (idx + 1) % 4 !== 0 && (
                  <div className="hidden lg:flex absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 items-center justify-center z-10">
                    <i className="ri-arrow-right-s-line text-green-300 text-xl" />
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/ja/contact/form" className="whitespace-nowrap inline-flex items-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-semibold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer">
              第一歩を踏み出す — 今すぐ相談 <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </section>

      {/* ODM vs OEM */}
      <section className="py-20 bg-[#f7faf7]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">
              提携モード
            </span>
            <h2 className="text-[#1a2e1a] font-bold mb-4">ODM vs OEM — あなたに合ったモードを選ぼう</h2>
            <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">
              康永生技は2つの主要な提携モードを提供。ニーズとブランドの発展段階に応じて柔軟に選択できます。
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {modes.map((m) => (
              <div key={m.mode} className="rounded-2xl p-8 border-2 transition-all duration-300" style={{ backgroundColor: m.bgColor, borderColor: m.borderColor }}>
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <span className="inline-block text-white font-bold text-[1.375rem] px-5 py-2 rounded-xl mb-3" style={{ backgroundColor: m.color }}>{m.mode}</span>
                    <p className="text-gray-400 text-[0.8125rem]">{m.fullName}</p>
                    <h3 className="text-[#1a2e1a] font-bold text-[1.25rem] mt-2">{m.title}</h3>
                  </div>
                </div>
                <p className="text-gray-600 text-[0.9375rem] leading-[1.75] mb-5">{m.desc}</p>
                <div className="inline-block text-[0.8125rem] font-medium px-3 py-1.5 rounded-full mb-5" style={{ backgroundColor: `${m.color}18`, color: m.color }}>
                  ✓ {m.suitable}
                </div>
                <ul className="space-y-2.5">
                  {m.points.map((pt) => (
                    <li key={pt} className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-checkbox-circle-fill text-lg" style={{ color: m.color }} />
                      </div>
                      <span className="text-gray-600 text-[0.9375rem] leading-[1.75]">{pt}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="mt-10 bg-white rounded-2xl p-8 border border-gray-100">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
              <div>
                <h3 className="text-[#1a2e1a] font-bold text-[1.125rem] mb-2">どちらのモードが合うか迷っていますか？</h3>
                <p className="text-gray-500 text-[0.9375rem]">営業チームにお気軽にご相談ください。ご要件に合わせた最適な提携方式をご提案します。</p>
              </div>
              <Link to="/ja/contact/form" className="whitespace-nowrap bg-[#4CAF50] hover:bg-[#43a047] text-white font-semibold text-[1rem] px-8 py-4 rounded-full transition-all duration-300 cursor-pointer flex-shrink-0">
                無料相談
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Cooperation Advantages */}
      <section className="py-20 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">
              提携の優位性
            </span>
            <h2 className="text-[#1a2e1a] font-bold mb-4">康永生技と提携する6つの優位性</h2>
            <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">
              康永生技を選ぶことで、よりスムーズで安心なOEM/ODMの旅を実現できます。
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {advantages.map((a) => (
              <div key={a.title} className="flex gap-5 p-6 rounded-2xl bg-[#f7faf7] hover:bg-green-50 border border-transparent hover:border-green-200 transition-all duration-300">
                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-xl flex-shrink-0 border border-green-100">
                  <i className={`${a.icon} text-[#4CAF50] text-xl`} />
                </div>
                <div>
                  <h3 className="text-[#1a2e1a] font-bold text-[1rem] mb-2">{a.title}</h3>
                  <p className="text-gray-500 text-[0.875rem] leading-[1.75]">{a.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ — 8 Questions */}
      <section className="py-20 bg-[#f7faf7]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">
              よくある質問
            </span>
            <h2 className="text-[#1a2e1a] font-bold mb-4">提携前によく寄せられる質問</h2>
            <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">
              最もよくある提携に関する疑問をまとめました。他にご質問があればお気軽にお問い合わせください。
            </p>
          </div>
          <div className="max-w-3xl mx-auto space-y-3">
            {faqs.map((faq, idx) => (
              <div key={idx} className={`bg-white rounded-xl border transition-all duration-200 overflow-hidden ${openIdx === idx ? 'border-[#4CAF50]/40' : 'border-gray-100'}`}>
                <button
                  className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer"
                  onClick={() => setOpenIdx(openIdx === idx ? null : idx)}
                >
                  <span className={`text-[1rem] font-semibold leading-snug pr-4 ${openIdx === idx ? 'text-[#4CAF50]' : 'text-[#1a2e1a]'}`}>
                    {faq.q}
                  </span>
                  <div className={`w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0 transition-all duration-200 ${openIdx === idx ? 'bg-[#4CAF50] text-white' : 'bg-green-50 text-[#4CAF50]'}`}>
                    <i className={`text-sm ${openIdx === idx ? 'ri-subtract-line' : 'ri-add-line'}`} />
                  </div>
                </button>
                {openIdx === idx && (
                  <div className="px-6 pb-5">
                    <p className="text-gray-600 text-[0.9375rem] leading-[1.75]">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="relative rounded-3xl overflow-hidden mb-12">
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=aerial%20view%20of%20lush%20green%20forest%20landscape%20from%20above%2C%20vibrant%20rich%20emerald%20green%20nature%2C%20wellness%20health%20concept%20background%2C%20beautiful%20natural%20texture%20pattern&width=1400&height=400&seq=partner-cta-bg-01&orientation=landscape"
                alt="提携CTAバックグラウンド"
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-[#1a3a1a]/80" />
            </div>
            <div className="relative z-10 px-8 lg:px-14 py-14 text-center">
              <span className="inline-block bg-[#4CAF50]/25 border border-[#4CAF50]/50 text-green-300 text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5">
                提携を始める
              </span>
              <h2 className="text-white font-bold leading-tight mb-5">
                準備はできていますか？<br />あなたのアイデアを製品に変えましょう
              </h2>
              <p className="text-green-200 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto mb-8">
                規模の大小に関わらず、康永生技のチームはすべての提携機会を真剣に受け止め、最もプロフェッショナルなアドバイスとサービスをご提供します。
              </p>
              <Link to="/ja/contact/form" className="whitespace-nowrap inline-flex items-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-bold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer">
                お問い合わせフォームに記入する <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {contactOptions.map((opt) => (
              <div key={opt.label} className="rounded-2xl p-7 border border-gray-100 transition-all duration-300 hover:border-green-200 hover:-translate-y-1" style={{ backgroundColor: opt.bg }}>
                <div className="w-12 h-12 flex items-center justify-center rounded-xl mb-5" style={{ backgroundColor: `${opt.color}20` }}>
                  <i className={`${opt.icon} text-2xl`} style={{ color: opt.color }} />
                </div>
                <h3 className="text-[#1a2e1a] font-bold text-[1.0625rem] mb-2">{opt.label}</h3>
                <p className="text-gray-500 text-[0.875rem] leading-[1.75] mb-5">{opt.desc}</p>
                <a href={opt.href} rel="nofollow" className="whitespace-nowrap inline-flex items-center gap-2 font-semibold text-[0.9375rem] transition-colors duration-200 cursor-pointer" style={{ color: opt.color }}>
                  {opt.action} <i className="ri-arrow-right-line text-sm" />
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>
    </JaLayout>
  );
}
